/**
 * One poller for every custom hostname on the site, shared by the header chip,
 * the top panel and the language rows — instead of each row polling on its own.
 * Cost is one cheap CF API call per hostname every 5s while the page is open;
 * we keep polling after Live so SSL renewals are surfaced.
 */

import { useCallback, useEffect, useState } from 'react'

import type { IRoute, IWebsite } from '@glocal/schemas'

import type { DnsRecordLine } from '../../components/record-line'
import { trpc } from '../../trpc'

export interface CustomHostnameDetails {
	status: string
	hostname: string
	ssl?: {
		status: string
		txt_name?: string
		txt_value?: string
		validation_records?: { txt_name: string; txt_value: string }[]
	}
	ownership_verification?: { type: string; name: string; value: string }
}

/**
 * Per hostname id: `undefined` — not fetched yet, `null` — Cloudflare says the
 * hostname no longer exists (the owner can re-issue it), object — last status.
 */
export type DomainStatusMap = Record<string, CustomHostnameDetails | null | undefined>

export function useDomainStatuses(routes: IRoute[]): {
	statuses: DomainStatusMap
	loaded: boolean
	refresh: () => Promise<void>
} {
	// The signature keys the polling effect, so a route added or removed by a
	// revalidation restarts the loop with the new set.
	const key = routes
		.filter((r) => r.custom_hostname_id && !r.redirect)
		.map((r) => r.custom_hostname_id as string)
		.sort()
		.join(',')

	const [statuses, setStatuses] = useState<DomainStatusMap>({})
	const [loaded, setLoaded] = useState(false)

	const refresh = useCallback(async () => {
		const ids = key ? key.split(',') : []
		if (ids.length === 0) {
			setLoaded(true)
			return
		}
		const entries = await Promise.all(
			ids.map(async (id) => {
				try {
					const d = (await trpc().CloudflareService.GetCustomHostname.query(
						id
					)) as CustomHostnameDetails | null
					return [id, d] as const
				} catch {
					// Network error: keep the last known state for this hostname.
					return [id, undefined] as const
				}
			})
		)
		setStatuses((prev) => {
			const next: DomainStatusMap = {}
			for (const [id, d] of entries) {
				next[id] = d === undefined ? prev[id] : d
			}
			return next
		})
		setLoaded(true)
	}, [key])

	useEffect(() => {
		let cancelled = false
		const tick = () => {
			if (!cancelled) {
				void refresh()
			}
		}
		tick()
		const id = window.setInterval(tick, 5000)
		return () => {
			cancelled = true
			window.clearInterval(id)
		}
	}, [refresh])

	return { statuses, loaded, refresh }
}

/** DNS + SSL both verified — the hostname is serving traffic. */
export function isLive(details: CustomHostnameDetails | null | undefined): boolean {
	return details?.status === 'active' && details?.ssl?.status === 'active'
}

/**
 * The hostnames that have to be verified for this site to count as connected:
 * one per language in subdomain mode, the single apex in subfolder mode. A
 * subfolder site with no apex hostname is an external-CDN setup (mooz) — the
 * customer's CDN handles routing and SSL, nothing on our side to verify.
 */
export function siteSlots(website: IWebsite, routes: IRoute[]): IRoute[] {
	if (website.mode === 'subfolder') {
		const apex = routes.find((r) => r.custom_hostname_id && !r.lang && !r.redirect)
		return apex ? [apex] : []
	}
	return routes.filter((r) => r.custom_hostname_id && r.lang && !r.redirect)
}

/**
 * Whether the domain is pointed at us — a question about DNS only. It drives
 * the header chip and the top panel, and must never be muddied by what is
 * currently switched on or how much of the plan is used.
 */
export function domainReady(website: IWebsite, routes: IRoute[], statuses: DomainStatusMap): boolean {
	const slots = siteSlots(website, routes)
	if (website.mode === 'subfolder' && slots.length === 0) {
		return true
	}
	return slots.length > 0 && slots.every((r) => isLive(statuses[r.custom_hostname_id as string]))
}

/**
 * The records the owner has to add for one hostname, in the customer's words.
 * Cloudflare only reports aggregate hostname + SSL status, so `found` is the
 * coarse version: ownership records count as found once the hostname is
 * verified, the certificate record once SSL is active (brief §3.4).
 */
export function recordLinesFor(hostname: string, details: CustomHostnameDetails | null | undefined): DnsRecordLine[] {
	const verified = details?.status === 'active'
	const sslActive = details?.ssl?.status === 'active'

	const lines: DnsRecordLine[] = [
		{ kind: 'CNAME', role: 'routing', name: hostname, value: 'cname.nayda.pro', found: verified },
	]

	const own = details?.ownership_verification
	if (own?.name) {
		lines.push({
			kind: 'TXT',
			role: 'proves you own the domain',
			name: own.name,
			value: own.value,
			found: verified,
		})
	}

	const sslTxtName = details?.ssl?.txt_name || details?.ssl?.validation_records?.[0]?.txt_name
	const sslTxtValue = details?.ssl?.txt_value || details?.ssl?.validation_records?.[0]?.txt_value
	if (sslTxtName) {
		lines.push({
			kind: 'TXT',
			role: 'issues your certificate',
			name: sslTxtName,
			value: sslTxtValue || '',
			found: sslActive,
		})
	}

	return lines
}
